import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Sparkles, BarChart3, Quote, FileText, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { SearchParam } from '@common/ts/enums';
import {
  useListThreads,
  useGetThread,
  useCreateThread,
  useDeleteThread,
  useGetSuggestions,
  useAnalysisSchemaStatus,
  useAnalysisRunProgress,
} from '@/reactQuery/researchAssistant';
import { useRegenerateComparison } from '@/reactQuery/comparison';
import { useGetFilters } from '@/reactQuery/get';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import useTranslation from '@/hooks/useTranslation';
import useMediaQuery from '@/hooks/useMediaQuery';
import NonBlockingProgressStrip from '@/components/ui/NonBlockingProgressStrip';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import useResearchChat from './research_assistant/useResearchChat';
import ThreadList from './research_assistant/ThreadList';
import ChatThread from './research_assistant/ChatThread';
import ArtifactPanel from './research_assistant/ArtifactPanel';

// Artifact panel resize constraints. Min width keeps quote cards and
// data tables legible; max width prevents the chat column from becoming
// unusable. Persisted in localStorage so the user's preference survives
// navigation and page reloads.
const ARTIFACT_PANEL_MIN_WIDTH = 320;
const ARTIFACT_PANEL_MAX_WIDTH = 900;
const ARTIFACT_PANEL_DEFAULT_WIDTH = 420;
const ARTIFACT_PANEL_WIDTH_KEY = 'ra:artifactPanelWidth';

function clampArtifactPanelWidth(value: number): number {
  return Math.max(ARTIFACT_PANEL_MIN_WIDTH, Math.min(ARTIFACT_PANEL_MAX_WIDTH, value));
}

// Height of the fixed project navbar. Used as the baseline offset for
// RA's fixed positioning — the sticky results tab bar height is measured
// dynamically in ProjectResults and passed in via tabBarHeight.
const PROJECT_NAVBAR_HEIGHT = 55;

interface ResearchAssistantProps {
  isRerunLoading?: boolean;
  tabBarHeight?: number;
}

// TODO: fragile dual-default — ProjectResults defaults to 61, keep in sync or extract a shared constant
function ResearchAssistant({ isRerunLoading, tabBarHeight = 61 }: ResearchAssistantProps) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID) || null;

  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [showArtifacts, setShowArtifacts] = useState(false);
  const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);

  // Below the lg breakpoint the artifact panel is rendered in a right-side
  // Sheet slide-over instead of inline. isLg gates the inline layout branch.
  const isLg = useMediaQuery('(min-width: 1024px)');
  const [artifactSheetOpen, setArtifactSheetOpen] = useState(false);

  // When the viewport crosses above lg, close the narrow-width artifact
  // Sheet so it doesn't re-open unexpectedly if the user resizes back
  // below lg later.
  useEffect(() => {
    if (isLg) setArtifactSheetOpen(false);
  }, [isLg]);

  // Artifact panel width — initial value pulled from localStorage (safely,
  // so SSR/tests with no window still get the default). Changes are
  // persisted on drag end via the handle below.
  const [artifactPanelWidth, setArtifactPanelWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return ARTIFACT_PANEL_DEFAULT_WIDTH;
    const stored = window.localStorage.getItem(ARTIFACT_PANEL_WIDTH_KEY);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return Number.isFinite(parsed) ? clampArtifactPanelWidth(parsed) : ARTIFACT_PANEL_DEFAULT_WIDTH;
  });
  const artifactResizeStateRef = useRef<{
    startX: number;
    startWidth: number;
  } | null>(null);

  // Mouse-drag resize for the artifact panel. The handle lives on the
  // panel's left edge; dragging left expands it, dragging right shrinks
  // it. We subtract from startWidth so the panel follows the cursor.
  const handleArtifactResizeStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      artifactResizeStateRef.current = {
        startX: e.clientX,
        startWidth: artifactPanelWidth,
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const handleMouseMove = (ev: MouseEvent) => {
        const state = artifactResizeStateRef.current;
        if (!state) return;
        const delta = state.startX - ev.clientX;
        const nextWidth = clampArtifactPanelWidth(state.startWidth + delta);
        setArtifactPanelWidth(nextWidth);
      };
      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        // Persist the final width so it survives reloads. Use the
        // functional setter to read the up-to-date width from React
        // state (the closure captured the old value at mousedown time).
        try {
          setArtifactPanelWidth((current) => {
            window.localStorage.setItem(ARTIFACT_PANEL_WIDTH_KEY, String(current));
            return current;
          });
        } catch {
          // Ignore localStorage failures (quota exceeded, disabled, etc.)
        }
        artifactResizeStateRef.current = null;
      };
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [artifactPanelWidth],
  );

  // Track which thread we last loaded into local state. This avoids re-running
  // the load effect on every query invalidation (which happens after sendMessage
  // and would otherwise overwrite streaming state).
  const loadedThreadIdRef = useRef<string | null>(null);
  // Track previous artifacts.length so the auto-open effect only fires when
  // a NEW artifact arrives — not every time `showArtifacts` flips to false.
  // Without this guard, the close button would be useless because the effect
  // would re-open the panel on the next render.
  const prevArtifactCountRef = useRef(0);

  // ── Data fetching ─────────────────────────────────────────────────

  const { currentProject } = useCurrentProject();
  const { data: threads = [], isLoading: threadsLoading } = useListThreads(projectId, filterId);
  const { data: activeThread, isLoading: activeThreadLoading } = useGetThread(
    projectId,
    activeThreadId,
  );
  const { data: suggestions = [] } = useGetSuggestions(projectId, filterId);
  const { data: projectFilters = [] } = useGetFilters(projectId);
  const createThread = useCreateThread(projectId);
  const deleteThread = useDeleteThread(projectId);

  // B1.f — Pre-flight check: does this (project, filter) slot have the
  // rich analysis schema the Research Assistant needs?
  const { data: schemaStatus } = useAnalysisSchemaStatus(projectId, filterId);

  // D.8: poll the run-progress endpoint every 5s for closed-tab recovery
  // and the per-tab inline notice. The endpoint is added by the Wave A
  // backend agent.
  const { data: runProgress } = useAnalysisRunProgress(projectId);
  const isRunInFlight =
    !!runProgress && runProgress.phase !== 'idle' && runProgress.phase !== 'complete';

  // D.8 triple-stack suppression: when schema is outdated OR a run is
  // in flight, suppress D.2's full ANALYSIS_OUTDATED red banner in the
  // chat and replace with a quieter inline notice (LLM expert R1).
  const suppressAnalysisOutdatedBanner = (!!schemaStatus && !schemaStatus.current) || isRunInFlight;

  // D.7: in-chat comparison regen state
  const regenerateComparison = useRegenerateComparison();
  const [regenProgress, setRegenProgress] = useState<string | undefined>(undefined);
  const [regenStartedAt, setRegenStartedAt] = useState<string | undefined>(undefined);
  // Graceful-fallback (D.7 Arch-R1): detect substep events within 2s of
  // regen kickoff. The regenSubstep state tracks the latest substep label
  // from the progress strip. When no substep arrives within 2s, the strip
  // degrades to a single elapsed-time line.
  const [regenSubstep, setRegenSubstep] = useState<string | undefined>(undefined);

  // Resolve filter name from filter_id. The FiltersDataToSendProps type
  // in the global declaration is incomplete (missing _id), so we cast
  // through unknown to a narrow inline type here.
  const filterName = useMemo(() => {
    if (!filterId) return null;
    const filters = projectFilters as unknown as Array<{
      _id: string;
      name: string;
    }>;
    const f = filters.find((x) => x._id === filterId);
    return f?.name || null;
  }, [filterId, projectFilters]);

  // Get respondent count from project metadata
  const respondentCount = currentProject?.respondents_count;
  const interviewGoal = currentProject?.interview_goal;

  // ── Chat hook ─────────────────────────────────────────────────────

  const {
    messages,
    artifacts,
    isStreaming,
    error,
    sendMessage,
    stopStreaming,
    retry,
    setMessages,
    setArtifacts,
    activeToolSteps,
  } = useResearchChat(projectId, activeThreadId, filterId, () => {
    if (document.hidden || !document.hasFocus()) {
      toast.success(lang.get('msg.raResultsReady'));
    }
  });

  // Local error state copy so we can dismiss it. The chat hook owns the
  // primary error state but we shadow it here to allow the user to clear it.
  const [dismissedError, setDismissedError] = useState<string | null>(null);
  const visibleError = error && error !== dismissedError ? error : null;
  const handleDismissError = useCallback(() => {
    if (error) setDismissedError(error);
  }, [error]);
  const handleRetry = useCallback(() => {
    setDismissedError(null);
    retry();
  }, [retry]);

  // ── Reset thread selection when filter changes ────────────────────
  // When the user switches the dashboard filter, the threads list refetches
  // for the new filter scope. We must clear activeThreadId so the auto-select
  // effect picks the most recent thread for the new filter — otherwise the
  // user would still see a thread from the old filter scope.
  const prevFilterIdRef = useRef(filterId);
  useEffect(() => {
    if (prevFilterIdRef.current !== filterId) {
      stopStreaming();
      setActiveThreadId(null);
      setMessages([]);
      setArtifacts([]);
      setShowArtifacts(false);
      loadedThreadIdRef.current = null;
      prevFilterIdRef.current = filterId;
    }
  }, [filterId, stopStreaming, setMessages, setArtifacts]);

  // ── Auto-select most recent thread ────────────────────────────────

  useEffect(() => {
    if (!activeThreadId && threads.length > 0) {
      setActiveThreadId(threads[0]._id);
    }
  }, [threads, activeThreadId]);

  // ── Load thread messages when thread changes ──────────────────────
  // Only loads on actual thread switch, not on every query invalidation.
  // This preserves streaming state during/after sendMessage.
  useEffect(() => {
    if (!activeThread) return;
    // String comparison handles both ObjectId-stringified _id and plain string.
    const activeId = String(activeThread._id);
    if (loadedThreadIdRef.current === activeId) return;

    const extractMessageText = (
      content: string | Array<{ type: string; text?: string }> | unknown,
    ): string => {
      if (typeof content === 'string') return content;
      if (Array.isArray(content)) {
        return (content as Array<{ type: string; text?: string }>)
          .filter((b) => b.type === 'text')
          .map((b) => b.text ?? '')
          .join('');
      }
      return '';
    };

    const chatMessages = activeThread.messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m, idx) => {
        // Extract text from string OR array content blocks
        const text = extractMessageText(m.content);
        return {
          id: `stored-${activeId}-${idx}`,
          role: m.role as 'user' | 'assistant',
          content: text,
          created_at: m.created_at,
        };
      })
      // Drop assistant messages that have NO text content (i.e., tool_use-only
      // intermediate messages from a multi-step turn). These would render as
      // empty chat bubbles. Their tool steps are not reconstructed for loaded
      // threads, but the final assistant response (which contains the actual
      // answer) IS preserved because it has text content.
      .filter((m) => m.role !== 'assistant' || m.content.length > 0);
    setMessages(chatMessages);
    setArtifacts(activeThread.artifacts || []);
    loadedThreadIdRef.current = activeId;
  }, [activeThread, setMessages, setArtifacts]);

  // ── Auto-open artifact panel when a NEW artifact arrives ─────────
  // Critical: only open when artifacts.length GREW since last render. If
  // we open whenever artifacts.length > 0 && !showArtifacts, the close
  // button would be useless because the effect would re-open immediately.
  // At narrow widths, the same growth condition auto-opens the Sheet —
  // if the user deliberately closes it during streaming, the NEXT
  // artifact still re-opens because each new artifact grows the count.
  useEffect(() => {
    if (artifacts.length > prevArtifactCountRef.current) {
      setShowArtifacts(true);
      setArtifactSheetOpen(true);
    }
    prevArtifactCountRef.current = artifacts.length;
  }, [artifacts.length]);

  // ── Handlers ──────────────────────────────────────────────────────

  // D.4 — pending-action state for the abort-confirm dialog.
  // When the user tries to switch threads OR delete the active thread
  // while a stream is in flight, we defer the action and show a confirm
  // dialog rather than silently aborting. After confirmation the action
  // proceeds; on cancel the user keeps streaming.
  type PendingAbortAction =
    | { type: 'switch'; threadId: string }
    | { type: 'delete'; threadId: string };
  const [pendingAbortAction, setPendingAbortAction] = useState<PendingAbortAction | null>(null);

  // Snapshot of the in-flight tool at the moment the dialog is opened
  // (so the elapsed counter doesn't tick the dialog body). We deliberately
  // freeze it on open instead of recomputing — the user reads it once.
  const [abortDialogToolInfo, setAbortDialogToolInfo] = useState<{
    name: string;
    elapsedSec: number | null;
  } | null>(null);

  const captureInFlightToolInfo = useCallback(() => {
    // Skip synthetic backend placeholder steps whose name starts with
    // `_` (currently `_thinking` between Anthropic iterations). These
    // are spinner stand-ins, not real tools — surfacing them in the
    // abort-confirm dialog reads as "running _thinking (10s elapsed)",
    // which leaks an internal implementation detail into a user-facing
    // string. Returning null falls through to the no-tool dialog
    // copy: "The assistant is generating a response."
    const running = activeToolSteps.find(
      (s) => s.status === 'running' && !s.tool_name.startsWith('_'),
    );
    if (!running) return null;
    return {
      name: running.tool_name,
      elapsedSec: running.startedAt
        ? Math.max(0, Math.floor((Date.now() - running.startedAt) / 1000))
        : null,
    };
  }, [activeToolSteps]);

  const performThreadSwitch = useCallback((threadId: string) => {
    setActiveThreadId(threadId);
    setShowArtifacts(false);
    loadedThreadIdRef.current = null;
  }, []);

  const performThreadDelete = useCallback(
    async (threadId: string) => {
      setDeletingThreadId(threadId);
      try {
        await deleteThread.mutateAsync(threadId);
        if (activeThreadId === threadId) {
          const nextThread = threads.find((t) => t._id !== threadId);
          setActiveThreadId(nextThread?._id ?? null);
          setMessages([]);
          setArtifacts([]);
          setShowArtifacts(false);
          loadedThreadIdRef.current = null;
        }
      } finally {
        setDeletingThreadId(null);
      }
    },
    [deleteThread, activeThreadId, threads, setMessages, setArtifacts],
  );

  const handleSelectThread = useCallback(
    (threadId: string) => {
      // D.4 — confirm before aborting an in-flight stream. Pre-D.4 we
      // silently called stopStreaming() which threw away partial work
      // (sometimes a quote_collection or analysis page that took 30s+
      // to generate). Now the user sees a dialog and chooses.
      if (isStreaming && threadId !== activeThreadId) {
        setAbortDialogToolInfo(captureInFlightToolInfo());
        setPendingAbortAction({ type: 'switch', threadId });
        return;
      }
      stopStreaming();
      performThreadSwitch(threadId);
    },
    [isStreaming, activeThreadId, stopStreaming, performThreadSwitch, captureInFlightToolInfo],
  );

  const handleNewThread = useCallback(async () => {
    stopStreaming();
    const result = await createThread.mutateAsync(filterId);
    setActiveThreadId(result._id);
    setMessages([]);
    setArtifacts([]);
    setShowArtifacts(false);
    loadedThreadIdRef.current = result._id;
  }, [createThread, filterId, setMessages, setArtifacts, stopStreaming]);

  const handleDeleteThread = useCallback(
    async (threadId: string) => {
      // D.4 — confirm before aborting an in-flight stream for the
      // ACTIVE thread. Deleting an inactive thread is benign and
      // doesn't abort streaming, so we proceed without prompting.
      if (isStreaming && activeThreadId === threadId) {
        setAbortDialogToolInfo(captureInFlightToolInfo());
        setPendingAbortAction({ type: 'delete', threadId });
        return;
      }
      await performThreadDelete(threadId);
    },
    [isStreaming, activeThreadId, performThreadDelete, captureInFlightToolInfo],
  );

  const confirmAbortAndExecute = useCallback(async () => {
    if (!pendingAbortAction) return;
    const action = pendingAbortAction;
    setPendingAbortAction(null);
    setAbortDialogToolInfo(null);
    stopStreaming();
    if (action.type === 'switch') {
      performThreadSwitch(action.threadId);
    } else {
      await performThreadDelete(action.threadId);
    }
  }, [pendingAbortAction, stopStreaming, performThreadSwitch, performThreadDelete]);

  const cancelAbort = useCallback(() => {
    setPendingAbortAction(null);
    setAbortDialogToolInfo(null);
  }, []);

  const handleSendMessage = useCallback(
    async (message: string) => {
      try {
        // Auto-create thread if none selected. Pass the new threadId directly
        // to sendMessage so we don't depend on React state propagation.
        if (!activeThreadId) {
          const result = await createThread.mutateAsync(filterId);
          setActiveThreadId(result._id);
          loadedThreadIdRef.current = result._id;
          await sendMessage(message, result._id);
          return;
        }
        await sendMessage(message);
      } catch (err: unknown) {
        // Errors from createThread or sendMessage would otherwise be
        // swallowed. The chat hook handles its own errors via setError,
        // but createThread mutations need explicit handling.
        // eslint-disable-next-line no-console
        console.error('Failed to send message:', err);
      }
    },
    [activeThreadId, createThread, filterId, sendMessage],
  );

  // D.5 — precompute the Outputs-affordance display state. Extracted
  // from the JSX to avoid nested ternaries (lint) and IIFE component
  // creation (react/no-unstable-nested-components).
  const outputsHasArtifacts = artifacts.length > 0;
  let outputsAriaLabel: string;
  if (outputsHasArtifacts) {
    outputsAriaLabel = `${lang.get('msg.raOpenArtifacts')} (${artifacts.length})`;
  } else if (isStreaming) {
    outputsAriaLabel = lang.get('msg.raGeneratingArtifact');
  } else {
    outputsAriaLabel = lang.get('msg.raOutputsPanel');
  }
  let outputsBadge: React.ReactNode;
  if (outputsHasArtifacts) {
    outputsBadge = artifacts.length;
  } else if (isStreaming) {
    outputsBadge = '…';
  } else {
    outputsBadge = lang.get('msg.raOutputsLabel');
  }
  const outputsShowSpinner = !outputsHasArtifacts && isStreaming;

  // ── Layout ────────────────────────────────────────────────────────
  // Full-bleed: no rounded card, no shadow, no outer border.
  //
  // We use `fixed` positioning pinned to `top` = navbar + tab bar so the
  // Research Assistant sits exactly below the fixed header + sub-tabs
  // chrome and fills the rest of the viewport down to the bottom edge.
  // This bypasses the flex chain of main → TabsContent entirely:
  //   - no flex-height math to chase
  //   - no dependency on parent `h-full` resolving correctly
  //   - no interaction with the `p-[2%]` padding on <main> because
  //     fixed elements are positioned relative to the viewport, not
  //     the padded parent box
  //   - no outer-page scroll because the element is out of flow
  //
  // The top offset is computed dynamically from the project navbar
  // constant plus the measured tab bar height passed in from
  // ProjectResults. This handles tab bar wrapping at narrow widths
  // without hardcoding a fallback.
  //
  // No border-t: the sub-tabs bar already has border-b; stacking
  // another border would produce a double divider line.
  return (
    <div
      style={{ top: PROJECT_NAVBAR_HEIGHT + tabBarHeight }}
      className="fixed left-0 right-0 bottom-0 flex overflow-hidden bg-white"
    >
      {/* Thread list sidebar — Sprint A audit fix (B-2): lowered the
          visibility breakpoint from lg (1024px) to md (768px) so users
          on common laptop resolutions (1280px logical at 125% browser
          zoom = ~1024px effective) still see the thread switcher.
          Below md the sidebar is hidden — sub-tablet screens are out
          of scope for the dashboard chrome anyway. */}
      <div className="w-[240px] shrink-0 hidden md:block">
        <ThreadList
          threads={threads}
          activeThreadId={activeThreadId}
          isLoading={threadsLoading}
          onSelectThread={handleSelectThread}
          onNewThread={handleNewThread}
          onDeleteThread={handleDeleteThread}
          isCreating={createThread.isPending}
          deletingThreadId={deletingThreadId}
          analyzedAt={schemaStatus?.analyzed_at}
        />
      </div>

      {/* Chat thread — flexible center */}
      <div className="flex-1 min-w-0">
        <ChatThread
          messages={messages}
          isStreaming={isStreaming}
          activeToolSteps={activeToolSteps}
          error={visibleError}
          onSendMessage={handleSendMessage}
          onStopStreaming={stopStreaming}
          onDismissError={handleDismissError}
          onRetry={handleRetry}
          // D.8: onReRunAnalysis is removed (Sprint-2 patch was wiring it to
          // re-show OutdatedAnalysisDialog, which D.8 deletes). The
          // ANALYSIS_OUTDATED banner now suppresses to a quieter inline notice
          // when D.8's sticky banner is visible. The prop is no longer passed
          // so ResearchAssistantErrorBanner's "resolveAnalysis" button is hidden
          // for ANALYSIS_OUTDATED — the per-tab inline notice below is the
          // canonical affordance. The banner's other 8 codes are unaffected.
          onOpenThreadList={() => {
            // Below md the thread list is hidden — for now, clearing the
            // active thread takes the user back to the picker / empty state.
            setActiveThreadId(null);
          }}
          suppressAnalysisOutdatedBanner={suppressAnalysisOutdatedBanner}
          onRegenerateComparison={async (_comparisonId, filterIdA, filterIdB) => {
            // D.7: kick off in-chat comparison regen. The backend's
            // `next_action` payload now carries filter_id_a / filter_id_b
            // from the stale comparison doc, so the frontend can call the
            // existing regen endpoint directly without a comparison-id lookup.
            // Reconciled in Wave C verification.
            if (!filterIdA || !filterIdB) {
              // Guard: legacy event payloads without filter ids should fall
              // back to the manual Compare-tab flow. Surface a toast and bail.
              toast.error(
                lang.get('msg.regenMissingFilterIds') ||
                  'Cannot regenerate from chat — open the Compare tab.',
              );
              return;
            }
            setRegenStartedAt(new Date().toISOString());
            setRegenSubstep(undefined);
            try {
              await regenerateComparison.mutateAsync({
                projectId: projectId!,
                filterIdA,
                filterIdB,
                onProgress: (progress) => setRegenProgress(progress),
              });
            } finally {
              setRegenStartedAt(undefined);
              setRegenProgress(undefined);
              setRegenSubstep(undefined);
            }
          }}
          isRegeneratingComparison={regenerateComparison.isPending || isRunInFlight}
          regenSubstep={regenSubstep || regenProgress}
          regenStartedAt={regenStartedAt}
          projectTitle={currentProject?.title}
          filterName={filterName}
          interviewGoal={interviewGoal}
          respondentCount={respondentCount}
          suggestions={suggestions}
          isLoadingThread={activeThreadLoading && !messages.length}
          isCreatingThread={createThread.isPending}
          isRerunLoading={isRerunLoading}
          allArtifacts={artifacts}
          projectId={projectId}
          headerRight={
            // D.5 — persistent Outputs affordance below lg.
            //
            // Pre-D.5 the trigger only appeared when `isStreaming ||
            // artifacts.length > 0` so on 768-1023 viewports (typical
            // tablet / narrow laptop) the user had no visible entry
            // point to the artifact panel until the FIRST artifact was
            // generated mid-stream. They could happily chat for several
            // turns assuming "Outputs" didn't exist on their device.
            //
            // Fix: always render the trigger at md-only. Adapts label:
            //   - Has artifacts → Sparkles + count
            //   - Streaming, no artifacts yet → spinner + ellipsis
            //   - Otherwise (empty state) → Sparkles + localised
            //     "Outputs" label so the affordance is discoverable.
            isLg ? null : (
              <button
                type="button"
                onClick={() => setArtifactSheetOpen(true)}
                // Adapt label + glyph by state. Three modes:
                //   has artifacts → Sparkles + count
                //   streaming, none yet → spinner + ellipsis
                //   empty state → Sparkles + "Outputs"
                aria-label={outputsAriaLabel}
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-primary/10 border border-primary/20 text-primary text-xs font-semibold hover:bg-primary/15 transition-colors"
              >
                {outputsShowSpinner ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" />
                )}
                <span>{outputsBadge}</span>
              </button>
            )
          }
        />
      </div>

      {/* Artifact panel — right side, shown when artifacts exist.
          Resizable via the drag handle on its left edge; width is
          clamped to ARTIFACT_PANEL_MIN/MAX_WIDTH and persisted to
          localStorage so the user's preference survives navigation.
          Sprint A audit fix (B-2/H-6): hidden below lg (1024px) so
          standard laptops still see the panel. */}
      {showArtifacts && artifacts.length > 0 && (
        <div
          className="relative shrink-0 hidden lg:block"
          style={{ width: `${artifactPanelWidth}px` }}
        >
          {/* Drag handle — a thin 4px strip centred on the panel's
              left border. Hover widens the hit area slightly and
              adds a visible indicator so keyboard/screen-reader
              users know it's interactive. Positioned with -left-1
              so the handle straddles the border.
              Note: role="separator" is intentional — this IS a visual separator
              that is also interactive (resize). ARIA Authoring Practices Guide
              allows separator to be interactive when aria-valuenow is present.
              The tabIndex and event handlers are required for keyboard resize support. */}
          {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
          <div
            role="separator"
            aria-label="Resize artifact panel"
            aria-orientation="vertical"
            aria-valuenow={artifactPanelWidth}
            aria-valuemin={ARTIFACT_PANEL_MIN_WIDTH}
            aria-valuemax={ARTIFACT_PANEL_MAX_WIDTH}
            tabIndex={0}
            onMouseDown={handleArtifactResizeStart}
            onKeyDown={(e) => {
              // Left/right arrow keys resize by 16px increments for
              // keyboard users.
              if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setArtifactPanelWidth((w) => {
                  const next = clampArtifactPanelWidth(w + 16);
                  try {
                    window.localStorage.setItem(ARTIFACT_PANEL_WIDTH_KEY, String(next));
                  } catch {
                    /* ignore */
                  }
                  return next;
                });
              } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                setArtifactPanelWidth((w) => {
                  const next = clampArtifactPanelWidth(w - 16);
                  try {
                    window.localStorage.setItem(ARTIFACT_PANEL_WIDTH_KEY, String(next));
                  } catch {
                    /* ignore */
                  }
                  return next;
                });
              }
            }}
            className="absolute -left-1 top-0 bottom-0 w-2 cursor-col-resize z-10 group focus:outline-none"
          >
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-neutral-200 group-hover:bg-primary group-hover:w-0.5 group-focus-visible:bg-primary group-focus-visible:w-0.5 transition-colors" />
          </div>
          {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
          <ArtifactPanel
            artifacts={artifacts}
            projectId={projectId}
            onClose={() => setShowArtifacts(false)}
          />
        </div>
      )}

      {/* Reopen button when artifacts exist but panel is closed */}
      {!showArtifacts && artifacts.length > 0 && (
        <button
          type="button"
          onClick={() => setShowArtifacts(true)}
          aria-label={`${lang.get('msg.raOpenArtifacts')} (${artifacts.length})`}
          className="hidden lg:flex flex-col items-center justify-center gap-2 px-3 py-4 border-l border-neutral-200 bg-gradient-to-b from-primary/5 to-light-pink/30 hover:from-primary/10 hover:to-light-pink/40 transition-colors group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          <Sparkles className="h-4 w-4 text-primary group-hover:scale-110 transition-transform" />
          <span className="text-[10px] font-bold text-primary [writing-mode:vertical-rl] rotate-180 tracking-wider">
            {artifacts.length} {lang.get('msg.raArtifacts').toUpperCase()}
          </span>
        </button>
      )}

      {/* Artifact Sheet — below lg only. Uses modal={false} so the chat
          stays interactive while the sheet is open (researcher can ask
          follow-up questions while viewing artifacts). Explicit z-[100]
          ensures the sheet renders ABOVE the sticky z-[99] results
          tab bar. */}
      {!isLg && (
        <Sheet open={artifactSheetOpen} onOpenChange={setArtifactSheetOpen} modal={false}>
          <SheetContent
            side="right"
            className="w-full sm:w-[420px] sm:min-w-[380px] max-w-[560px] p-0 flex flex-col z-[100] [&>button:first-child]:hidden"
          >
            <SheetTitle className="sr-only">{lang.get('msg.raArtifacts')}</SheetTitle>
            {artifacts.length > 0 ? (
              <ArtifactPanel
                artifacts={artifacts}
                projectId={projectId}
                onClose={() => setArtifactSheetOpen(false)}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <Loader2 className="h-6 w-6 text-primary animate-spin mb-3" />
                <p className="text-sm font-medium text-neutral-700">
                  {lang.get('msg.generatingInsights')}
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  {lang.get('msg.generatingInsightsDesc')}
                </p>
              </div>
            )}
          </SheetContent>
        </Sheet>
      )}

      {/* D.8: per-tab inline notice when schema is not current (replaces
          OutdatedAnalysisDialog which is deleted as part of D.8).
          Shown when schema is outdated AND no full-pipeline run is in flight.
          When a run IS in flight, the D.8 sticky banner in ProjectResults
          already covers this feedback — rendering both would double-stack. */}
      {!!schemaStatus && !schemaStatus.current && !isRunInFlight && (
        <div className="absolute top-0 left-0 right-0 z-10">
          <NonBlockingProgressStrip
            variant="banner"
            title={lang.get('msg.raBuildingRichSchema')}
            startedAt={runProgress?.started_at}
          />
        </div>
      )}

      {/* D.8: per-tab inline notice while run IS in flight */}
      {isRunInFlight && (
        <div className="absolute top-0 left-0 right-0 z-10">
          <NonBlockingProgressStrip
            variant="banner"
            title={lang.get('msg.raBuildingRichSchemaRunning')}
            substep={runProgress?.phase !== 'idle' ? runProgress?.phase : undefined}
            current={runProgress?.current}
            total={runProgress?.total}
            startedAt={runProgress?.started_at}
          />
        </div>
      )}

      {/* Branded placeholder when no artifacts yet — fills the right side
          so the layout doesn't feel empty. When streaming, show a loading
          indicator so the user knows an artifact is on its way (the chat
          often says "see the artifact panel →" before the artifact arrives). */}
      {!showArtifacts && artifacts.length === 0 && (
        <div className="w-[280px] shrink-0 hidden lg:flex flex-col border-l border-neutral-200 bg-gradient-to-b from-neutral-50/50 to-primary/5 p-6">
          <div className="flex items-center justify-center h-12 w-12 rounded-2xl bg-white border border-neutral-200 shadow-sm mb-4 mx-auto">
            {isStreaming ? (
              <Loader2 className="h-5 w-5 text-primary animate-spin" />
            ) : (
              <Sparkles className="h-5 w-5 text-primary" />
            )}
          </div>
          <h3 className="text-sm font-semibold text-neutral-700 text-center mb-1">
            {isStreaming ? lang.get('msg.generatingInsights') : lang.get('msg.insightsAppearHere')}
          </h3>
          <p className="text-xs text-neutral-500 text-center mb-6 leading-relaxed">
            {isStreaming
              ? lang.get('msg.generatingInsightsDesc')
              : lang.get('msg.insightsAppearHereDesc')}
          </p>
          <div className="flex flex-col gap-2">
            {[
              {
                icon: Quote,
                label: lang.get('msg.quoteCollections'),
                color: 'text-chat-primary bg-light-pink border-chat-primary/30',
              },
              {
                icon: BarChart3,
                label: lang.get('msg.dataTablesCharts'),
                color: 'text-secondary bg-secondary/15 border-secondary/30',
              },
              {
                icon: FileText,
                label: lang.get('msg.markdownReports'),
                color: 'text-primary bg-primary/10 border-primary/20',
              },
            ].map((item) => (
              <div
                key={item.label}
                className={`flex items-center gap-2 text-[11px] font-medium px-2.5 py-1.5 rounded-lg border ${item.color}`}
              >
                <item.icon className="h-3 w-3" />
                {item.label}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* D.4 — Abort-confirm dialog for thread switch / delete while
          streaming. Pre-D.4 these handlers silently called
          stopStreaming(), throwing away partial work. The dialog lets
          the user see what's in flight and choose. */}
      <AlertDialog
        open={pendingAbortAction !== null}
        onOpenChange={(open) => {
          if (!open) cancelAbort();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{lang.get('msg.raAbortStreamTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {abortDialogToolInfo
                ? lang
                    .get('msg.raAbortStreamWithTool')
                    .replace('{toolName}', abortDialogToolInfo.name)
                    .replace(
                      '{elapsedSec}',
                      abortDialogToolInfo.elapsedSec != null
                        ? String(abortDialogToolInfo.elapsedSec)
                        : '?',
                    )
                : lang.get('msg.raAbortStreamNoTool')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelAbort}>{lang.get('msg.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAbortAndExecute}>
              {lang.get('msg.raAbortConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ResearchAssistant;
